import { useEffect, useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Plus, Trash2, Clock, RefreshCw, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { useCare, useLiveCare, errorText } from './CareContext';
import { FileButton } from './CareFileButton';
import { CareReceiptButton } from './CareReceiptButton';
import { getCachedProducts } from '../lib/offlineDB';
import { PortalRefillNudges } from '../portal/pages/PortalRefillNudges';

const PendingCareActions = () => {
  const { pendingActions, retryPendingAction } = useCare();
  const [retrying, setRetrying] = useState(null);
  if (!pendingActions.length) return null;
  const retry = async (localId) => {
    setRetrying(localId);
    try { await retryPendingAction(localId); } catch { /* toast already shown */ }
    setRetrying(null);
  };
  return (
    <div className="care-pending-actions border rounded-lg p-3 bg-amber-50 border-amber-300" data-testid="care-pending-actions">
      <p className="text-sm font-medium flex items-center gap-1.5 text-amber-800">
        <Clock size={14} /> {pendingActions.length} clinical decision(s) saved offline — not yet submitted
      </p>
      <p className="text-xs text-amber-700 mt-1">Tap submit once you&apos;re back online.</p>
      <div className="flex flex-wrap gap-2 mt-2">
        {pendingActions.map((a) => (
          <Button key={a.local_id} size="sm" variant="outline" disabled={retrying === a.local_id} onClick={() => retry(a.local_id)} data-testid={`care-pending-submit-${a.local_id}`}>
            <RefreshCw size={13} className={`mr-1.5 ${retrying === a.local_id ? 'animate-spin' : ''}`} /> Submit now
          </Button>
        ))}
      </div>
    </div>
  );
};

const waitingFor = (iso) => {
  if (!iso) return '—';
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ${mins % 60}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
};

// A single request row: reference/status header, note text, and the action
// buttons available to the current role for this request's current state.
const RequestRow = ({ row, patientMode, user, onReview, onQuote, onFlag, onClearFlag, flagging }) => {
  const canReview = !patientMode && (user?.role === 'pharmacist' || user?.role === 'super_admin')
    && row.care_version && (row.status !== 'collected' || row.request_type === 'prescription');
  const canQuote = !patientMode && (user?.role === 'pharmacist' || user?.role === 'super_admin')
    && row.clinical_status === 'valid' && row.status !== 'collected';
  const canOpenBillInPos = !patientMode && row.bill?.approval_current && row.status !== 'collected';
  const canOpenOtcInPos = !patientMode && row.request_type === 'otc' && !['collected', 'cancelled'].includes(row.status);
  const urgent = row.priority === 'urgent';
  // Any staff member working the counter can triage; closed requests cannot.
  const canTriage = !patientMode && !row.legacy && !['collected', 'cancelled'].includes(row.status);

  return (
    <article
      key={row.id}
      className={`care-record${urgent ? ' care-record-urgent' : ''}`}
      data-testid={`care-request-${row.id}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="care-origin" data-testid={`care-origin-${row.id}`}>Online · {row.request_type.toUpperCase()}</span>
        <span className="text-xs font-mono break-all" data-testid={`care-reference-${row.id}`}>{row.id}</span>
      </div>
      {urgent && (
        <p className="care-urgent-flag" data-testid={`care-urgent-badge-${row.id}`}>
          <ArrowUp size={13} />
          {patientMode ? 'Being prioritised' : 'Urgent'}
          {!patientMode && row.priority_by_name && <span className="care-urgent-meta">flagged by {row.priority_by_name}</span>}
          {!patientMode && row.priority_reason && <span className="care-urgent-meta">“{row.priority_reason}”</span>}
        </p>
      )}
      <div className="care-status-grid" data-testid={`care-states-${row.id}`}>
        <span>Clinical: <b>{row.clinical_status || (row.request_type === 'otc' ? 'OTC' : 'Legacy')}</b></span>
        <span>Quote: <b>{row.quote_approved ? 'Approved' : row.quote_id ? 'Awaiting patient' : 'Not issued'}</b></span>
        <span>Fulfilment: <b>{row.status}</b></span>
        {!patientMode && <span data-testid={`care-waiting-${row.id}`}>Waiting: <b>{waitingFor(row.created_at)}</b></span>}
        {row.valid_until && <span>Prescription valid until: <b>{row.valid_until}</b></span>}
      </div>
      {row.legacy ? (
        <p className="care-legacy" data-testid={`care-legacy-${row.id}`}>Legacy record predating the Care module — historical data only.</p>
      ) : (
        <>
          {row.note && <p className="whitespace-pre-wrap break-words text-sm" data-testid={`care-request-content-${row.id}`}>{row.note}</p>}
          {row.review?.note && <p className="whitespace-pre-wrap break-words text-sm border-t pt-2 mt-2" data-testid={`care-review-note-${row.id}`}>{row.review.note}</p>}
        </>
      )}
      <div className="flex flex-wrap gap-2 mt-3">
        <FileButton id={row.file_id} context={row.id} />
        <CareReceiptButton saleId={row.sale_id || row.bill?.sale_id} context={row.id} />
        {canTriage && (urgent ? (
          <Button variant="ghost" size="sm" disabled={flagging} onClick={() => onClearFlag(row)} data-testid={`care-clear-urgent-${row.id}`}>
            <ArrowDown size={14} className="mr-1.5" /> Clear urgent
          </Button>
        ) : (
          <Button variant="outline" size="sm" disabled={flagging} onClick={() => onFlag(row)} data-testid={`care-flag-urgent-${row.id}`}>
            <ArrowUp size={14} className="mr-1.5" /> Flag urgent
          </Button>
        ))}
        {canReview && (
          <Button variant="outline" size="sm" onClick={() => onReview(row)} data-testid={`care-review-${row.id}`}>
            {row.status === 'collected' ? 'Revoke future use' : 'Review'}
          </Button>
        )}
        {canQuote && <Button size="sm" onClick={() => onQuote(row)} data-testid={`care-quote-${row.id}`}>Prepare quote</Button>}
        {canOpenBillInPos && (
          <Button size="sm" asChild><Link to={'/pos?care_bill=' + row.bill.bill_id} data-testid={`care-pos-${row.id}`}>Open in POS</Link></Button>
        )}
        {canOpenOtcInPos && (
          <Button size="sm" asChild><Link to={'/pos?portal_otc=' + row.id} data-testid={`care-otc-pos-${row.id}`}>Open in POS</Link></Button>
        )}
      </div>
    </article>
  );
};

export const CareRequestsView = ({ only, exclude, patientId }) => {
  const { patientMode, user, api } = useCare();
  const { data, error, refresh } = useLiveCare('/requests' + (patientId ? '?patient_user_id=' + patientId : ''), { requests: [] });
  const [creating, setCreating] = useState(false);
  const [reviewing, setReviewing] = useState(null);
  const [quoting, setQuoting] = useState(null);
  const [flagging, setFlagging] = useState(null);     // row awaiting an urgent flag
  const [flagReason, setFlagReason] = useState('');
  const [flagBusy, setFlagBusy] = useState(false);
  const typeRows = data.requests.filter(row => (!only || row.request_type === only) && !(exclude || []).includes(row.request_type));
  const [urgentOnly, setUrgentOnly] = useState(false);
  const urgentCount = typeRows.filter(row => row.priority === 'urgent').length;
  const rows = urgentOnly ? typeRows.filter(row => row.priority === 'urgent') : typeRows;
  // A refill nudge from the pharmacy hands the medication over in router
  // state — open the request form straight away with it noted.
  const prefillNote = useLocation().state?.prefillNote;
  const showNudges = patientMode && only === 'refill';
  useEffect(() => {
    if (showNudges && prefillNote) setCreating(true);
  }, [showNudges, prefillNote]);

  const setPriority = async (row, urgent, reason = '') => {
    setFlagBusy(true);
    try {
      await api.post(`/requests/${row.id}/priority`, { urgent, reason });
      toast.success(urgent ? 'Moved up the queue' : 'Urgent flag cleared');
      setFlagging(null);
      setFlagReason('');
      refresh();
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setFlagBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      {showNudges && <PortalRefillNudges />}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2>{only === 'prescription' ? 'Prescription reviews' : 'Online orders'}</h2>
        <div className="flex flex-wrap items-center gap-2">
          {!patientMode && urgentCount > 0 && (
            <button
              type="button"
              className={`care-queue-chip${urgentOnly ? ' care-queue-chip-on' : ''}`}
              onClick={() => setUrgentOnly(v => !v)}
              data-testid="care-urgent-filter"
            >
              <ArrowUp size={13} /> {urgentCount} urgent{urgentOnly ? ' · showing' : ''}
            </button>
          )}
          {patientMode && only !== 'otc' && (
            <Button data-testid="care-new-request" onClick={() => setCreating(true)}>
              <Plus size={16} className="mr-2" />{only === 'refill' ? 'Request refill' : 'Submit prescription'}
            </Button>
          )}
        </div>
      </div>
      {!patientMode && <PendingCareActions />}
      {error && <p role="alert" data-testid="care-requests-error">{error}</p>}
      {!rows.length && (
        <p className="care-empty" data-testid="care-requests-empty">
          {urgentOnly ? 'Nothing flagged urgent in this branch.' : 'No requests in this branch.'}
        </p>
      )}
      {rows.map(row => (
        <RequestRow
          key={row.id}
          row={row}
          patientMode={patientMode}
          user={user}
          onReview={setReviewing}
          onQuote={setQuoting}
          onFlag={(target) => { setFlagReason(''); setFlagging(target); }}
          onClearFlag={(target) => setPriority(target, false)}
          flagging={flagBusy}
        />
      ))}

      <Dialog open={!!flagging} onOpenChange={open => !open && setFlagging(null)}>
        <DialogContent className="care-dialog" data-testid="care-flag-dialog" closeProps={{ 'data-testid': 'care-flag-close' }}>
          <DialogHeader>
            <DialogTitle>Move this request up the queue</DialogTitle>
            <DialogDescription>
              The patient sees only that they are being prioritised. Your reason stays internal.
            </DialogDescription>
          </DialogHeader>
          <label className="care-label">
            Reason (optional)
            <Textarea
              value={flagReason}
              onChange={e => setFlagReason(e.target.value)}
              maxLength={300}
              placeholder="e.g. waiting at the counter, antibiotic course due today"
              data-testid="care-flag-reason"
            />
          </label>
          <Button disabled={flagBusy} onClick={() => setPriority(flagging, true, flagReason)} data-testid="care-flag-submit">
            {flagBusy ? 'Flagging…' : 'Flag urgent'}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="care-dialog" data-testid="care-new-request-dialog" closeProps={{ 'data-testid': 'care-new-request-close' }}>
          <DialogHeader>
            <DialogTitle>{only === 'refill' ? 'Prescription-linked refill' : 'New prescription'}</DialogTitle>
            <DialogDescription>Share only with the receiving pharmacy branch.</DialogDescription>
          </DialogHeader>
          <RequestForm kind={only === 'refill' ? 'refill' : 'prescription'} rows={data.requests} initialNote={prefillNote} done={() => { setCreating(false); refresh(); }} />
        </DialogContent>
      </Dialog>
      <Dialog open={!!reviewing} onOpenChange={open => !open && setReviewing(null)}>
        <DialogContent className="care-dialog" data-testid="care-review-dialog" closeProps={{ 'data-testid': 'care-review-close' }}>
          <DialogHeader>
            <DialogTitle>Pharmacist decision</DialogTitle>
            <DialogDescription>Customer validation is separate from prescription validity.</DialogDescription>
          </DialogHeader>
          {reviewing && <ReviewForm row={reviewing} done={() => { setReviewing(null); refresh(); }} />}
        </DialogContent>
      </Dialog>
      <Dialog open={!!quoting} onOpenChange={open => !open && setQuoting(null)}>
        <DialogContent className="care-dialog" data-testid="care-quote-dialog" closeProps={{ 'data-testid': 'care-quote-close' }}>
          <DialogHeader>
            <DialogTitle>Patient cost approval</DialogTitle>
            <DialogDescription>Any changed price or medicine requires new patient approval.</DialogDescription>
          </DialogHeader>
          {quoting && <QuoteForm row={quoting} done={() => { setQuoting(null); refresh(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const RequestForm = ({ kind, rows, done, initialNote }) => {
  const { api, branch, uploadFile } = useCare();
  const { data: validation } = useLiveCare('/validation', {});
  const [file, setFile] = useState(null);
  const [text, setText] = useState(initialNote || '');
  const [rx, setRx] = useState('');
  const [exception, setException] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const current = validation.attestation?.status === 'approved' && validation.attestation?.expires_at > new Date().toISOString();

  // Memoised: filter eligible prescriptions once, not on every keystroke
  const eligibleRxs = useMemo(
    () => rows.filter(r => r.request_type === 'prescription' && r.care_version && r.reviewed_by),
    [rows]
  );

  if (kind === 'refill' && !current) {
    return <p role="alert" data-testid="care-refill-validation-required">Current customer validation is required. Open Verification to submit or renew it. Existing history remains available.</p>;
  }

  const submit = async e => {
    e.preventDefault();
    setBusy(true);
    try {
      let file_id;
      if (file) file_id = await uploadFile(file, 'prescription', branch);
      await api.post(kind === 'refill' ? '/refills' : '/prescriptions', {
        tenant_id: branch, note: text, file_id,
        ...(kind === 'refill' ? { rx_id: rx, request_exception: exception } : {}),
      });
      toast.success('Request sent to the receiving pharmacy');
      done();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="space-y-4" onSubmit={submit}>
      {error && <p role="alert" data-testid="care-request-form-error">{error}</p>}
      {kind === 'refill' && (
        <>
          <label className="care-label">
            Original prescription
            <select className="care-select" required value={rx} onChange={e => setRx(e.target.value)} data-testid="care-refill-source">
              <option value="">Choose prescription</option>
              {eligibleRxs.map(r => (
                <option key={r.id} value={r.id}>{r.id} · valid until {r.valid_until}</option>
              ))}
            </select>
          </label>
          <label className="care-check">
            <input type="checkbox" checked={exception} onChange={e => setException(e.target.checked)} data-testid="care-refill-exception-request" />
            Request a pharmacist-documented exception (not an automatic approval)
          </label>
        </>
      )}
      <label className="care-label">
        {kind === 'refill' ? 'Share prescription again for another pharmacy (if needed)' : 'Prescription document'}
        <Input type="file" accept="image/png,image/jpeg,image/webp,application/pdf" required={kind !== 'refill'} onChange={e => setFile(e.target.files?.[0])} data-testid="care-rx-file" />
      </label>
      <label className="care-label">
        Message for the pharmacist
        <Textarea required value={text} onChange={e => setText(e.target.value)} maxLength={4000} data-testid="care-rx-message" />
      </label>
      <Button type="submit" disabled={busy || !branch} data-testid="care-request-submit">{busy ? 'Submitting…' : 'Submit'}</Button>
    </form>
  );
};

const ReviewApprovedItems = ({ items, onChange }) => (
  <>
    {items.map((item, n) => (
      <div className="space-y-2 border-b pb-3" key={item.product_id}>
        <p className="text-sm" data-testid={`care-approved-name-${n}`}>{item.name || item.product_id}</p>
        <div className="flex gap-2">
          <label className="care-label flex-1">
            Quantity
            <Input
              type="number" required min="1" max="999" value={item.quantity}
              onChange={e => onChange(items.map((x, idx) => idx === n ? { ...x, quantity: e.target.value } : x))}
              data-testid={`care-approved-quantity-${n}`}
            />
          </label>
          <label className="care-label flex-1">
            Per-refill allowance
            <Input
              type="number" min="0" max="9999" value={item.refill_quantity}
              onChange={e => onChange(items.map((x, idx) => idx === n ? { ...x, refill_quantity: e.target.value } : x))}
              data-testid={`care-approved-refill-${n}`}
            />
          </label>
          <Button type="button" size="icon" variant="ghost" title="Remove item" aria-label="Remove item" onClick={() => onChange(items.filter((_, idx) => idx !== n))} data-testid={`care-approved-remove-${n}`}>
            <Trash2 size={16} />
          </Button>
        </div>
      </div>
    ))}
  </>
);

const ReviewForm = ({ row, done }) => {
  const { api, queueOrSend } = useCare();
  const [action, setAction] = useState(row.status === 'collected' ? 'revoke' : 'valid');
  const [until, setUntil] = useState(row.valid_until || '');
  const [refills, setRefills] = useState(row.refill_allowance || 0);
  const [text, setText] = useState('');
  const [items, setItems] = useState(row.approved_items || []);
  const [query, setQuery] = useState('');
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [exception, setException] = useState(false);
  const [match, setMatch] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    const t = setTimeout(() => {
      api.get('/products', { params: { q: query }, signal: controller.signal }).then(r => setProducts(r.data.products)).catch(() => {});
    }, 250);
    return () => { clearTimeout(t); controller.abort(); };
  }, [query, api]);

  const addApprovedItem = (productId) => {
    const p = products.find(x => x.product_id === productId);
    if (p && !items.some(i => i.product_id === p.product_id)) {
      setItems([...items, { product_id: p.product_id, name: p.name, quantity: 1, refill_quantity: 0 }]);
    }
  };

  const submit = async e => {
    e.preventDefault();
    setBusy(true);
    try {
      let queued = false;
      if (action === 'revoke') {
        ({ queued } = await queueOrSend('post', `/prescriptions/${row.id}/revoke`, { note: text }));
      } else {
        if (exception) await queueOrSend('post', `/refills/${row.id}/exception`, { reason_confirmed: true, note: text });
        ({ queued } = await queueOrSend('post', `/requests/${row.id}/review`, {
          action, valid_until: until || null, refill_count: Number(refills),
          items: items.map(i => ({ product_id: i.product_id, quantity: Number(i.quantity), refill_quantity: Number(i.refill_quantity || 0) })),
          note: text, cross_pharmacy_match_confirmed: match,
        }));
      }
      if (queued) toast.success("Decision saved offline — submit it from Pending clinical actions once you're back online.");
      done();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="space-y-4" onSubmit={submit}>
      {error && <p role="alert" data-testid="care-review-error">{error}</p>}
      <FileButton id={row.file_id} context={'review-' + row.id} />
      <label className="care-label">
        Decision
        <select className="care-select" value={action} onChange={e => setAction(e.target.value)} data-testid="care-review-action">
          {row.status !== 'collected' && (
            <>
              <option value="valid">Valid</option>
              <option value="clarification">Needs clarification</option>
              <option value="invalid">Invalid</option>
            </>
          )}
          {row.request_type === 'prescription' && <option value="revoke">Revoke future use — retain dispensing history</option>}
        </select>
      </label>
      <label className="care-label">
        Patient-visible reason / clinical notes
        <Textarea required value={text} onChange={e => setText(e.target.value)} data-testid="care-review-reason" />
      </label>
      {action === 'valid' && (
        <>
          <div className="care-form-grid">
            <label className="care-label">
              Actual prescription valid until
              <Input type="date" required value={until} onChange={e => setUntil(e.target.value)} data-testid="care-valid-until" />
            </label>
            <label className="care-label">
              Number of permitted refills
              <Input type="number" min="0" max="100" value={refills} onChange={e => setRefills(e.target.value)} data-testid="care-refill-count" />
            </label>
          </div>
          <Input placeholder="Search approved medicine and strength" value={query} onChange={e => setQuery(e.target.value)} data-testid="care-review-product-search" />
          <select className="care-select" value="" onChange={e => addApprovedItem(e.target.value)} data-testid="care-review-product">
            <option value="">Add approved medicine / strength</option>
            {products.map(p => <option key={p.product_id} value={p.product_id}>{p.name} {p.strength || ''}</option>)}
          </select>
          <ReviewApprovedItems items={items} onChange={setItems} />
        </>
      )}
      {row.request_type === 'refill' && (
        <>
          <label className="care-check">
            <input type="checkbox" checked={exception} onChange={e => setException(e.target.checked)} data-testid="care-authorise-exception" />
            Authorise this specific exception, with the reason above. This does not override prescribing law.
          </label>
          <label className="care-check">
            <input type="checkbox" checked={match} onChange={e => setMatch(e.target.checked)} data-testid="care-cross-pharmacy-match" />
            I compared the shared original prescription and confirmed matching medicines, strengths and quantity allowances.
          </label>
        </>
      )}
      <Button type="submit" disabled={busy} data-testid="care-review-submit">Save clinical decision</Button>
    </form>
  );
};

const QuoteForm = ({ row, done }) => {
  const { api, queueOrSend } = useCare();
  const [expiry, setExpiry] = useState('');
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [priced, setPriced] = useState([]);

  useEffect(() => {
    const ids = row.approved_items.map(i => i.product_id);
    api.get('/products', { params: { ids: ids.join(',') } })
      .then(r => setPriced(row.approved_items.map(i => ({
        ...i,
        ...r.data.products.find(p => p.product_id === i.product_id),
        unit_minor: Math.round(Number(r.data.products.find(p => p.product_id === i.product_id)?.unit_price) * 100),
      }))))
      .catch(async (e) => {
        if (!e.response) {
          // Offline — reuse the same cached product catalog the POS terminal uses.
          const cached = await getCachedProducts();
          setPriced(row.approved_items.map(i => {
            const p = cached.find(c => c.product_id === i.product_id);
            return { ...i, ...p, unit_minor: p ? Math.round(Number(p.unit_price) * 100) : null };
          }));
        } else {
          setError(errorText(e));
        }
      });
  }, [api, row]);

  const submit = async e => {
    e.preventDefault();
    setBusy(true);
    try {
      if (priced.length !== row.approved_items.length || priced.some(i => !Number.isInteger(i.unit_minor))) {
        throw new Error('Current product prices are not available');
      }
      const { queued } = await queueOrSend('post', `/requests/${row.id}/quote`, {
        items: priced.map(i => ({ product_id: i.product_id, quantity: i.quantity, unit_minor: i.unit_minor })),
        expires_at: new Date(expiry).toISOString(), note: text,
      });
      if (queued) toast.success("Quote saved offline — submit it from Pending clinical actions once you're back online.");
      done();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="space-y-4" onSubmit={submit}>
      {error && <p role="alert" data-testid="care-quote-error">{error}</p>}
      <p className="text-sm" data-testid="care-quote-items">{row.approved_items.map(i => `${i.name || i.product_id} × ${i.quantity}`).join('\n')}</p>
      <p className="text-xs text-muted-foreground">Current branch prices will be included. The patient must approve the itemised quote.</p>
      <label className="care-label">
        Quote expires
        <Input type="datetime-local" required value={expiry} onChange={e => setExpiry(e.target.value)} data-testid="care-quote-expiry" />
      </label>
      <label className="care-label">
        Private quote details / substitutions
        <Textarea required value={text} onChange={e => setText(e.target.value)} data-testid="care-quote-note" />
      </label>
      <Button type="submit" disabled={busy} data-testid="care-quote-submit">Send quote for approval</Button>
    </form>
  );
};
